/**
 * The post a repost shares, inside the repost: as its author published it, from the server's `original`
 * (only when the viewer may see it now; otherwise it says the post is unavailable or deleted).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Chip, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import LabMarkdown, { SafeImage } from 'components/collab/lab/LabMarkdown';
import { linkedImageOf, postMarkdown } from 'shared/helpers/postText';
import { findYouTube, parseYouTube } from 'shared/helpers/youtube';
import LinkPreviewCard from './LinkPreviewCard';
import YouTubeEmbed from './YouTubeEmbed';

const SEEKING = { 0: 'Seeking a consortium', 1: 'Seeking partners' };

/** A reposted interest: who is seeking what, on which call; a click opens the call. */
function RepostedInterest({ original, name, linkable }) {
  const navigate = useNavigate();
  const author = original.author || {};
  const open = () => original.call_id && navigate(`/funding/call/${original.call_id}/`);
  return (
    <Box {...(linkable && { role: 'link', tabIndex: 0, onClick: (e) => { e.stopPropagation(); open(); },
      onKeyDown: (e) => { if (e.key === 'Enter') open(); } })}
      sx={{ mt: 1, mr: linkable ? { xs: 0, sm: 2 } : 0, minWidth: 0, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', ...(linkable && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }) }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
        <Avatar src={author.avatar || undefined} sx={{ width: 26, height: 26, fontSize: 13 }}>{name[0]}</Avatar>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{name}</Typography>
        {SEEKING[original.type_of_interest] && (
          <Chip size="small" label={SEEKING[original.type_of_interest]} color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        <Typography variant="caption" color="text.secondary">
          {original.created ? formatDistanceToNow(new Date(original.created), { addSuffix: true }) : ''}
        </Typography>
      </Box>
      {original.call_title && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>on {original.call_title}</Typography>
      )}
      <Box sx={{ maxHeight: '8em', overflow: 'hidden', wordBreak: 'break-word', '& p': { mb: 0.75 } }}>
        <LabMarkdown images hashtags>{postMarkdown(original.body)}</LabMarkdown>
      </Box>
    </Box>
  );
}

/** LINKABLE: a click opens the original's page (off where it is only shown, e.g. the repost dialog). */
export default function RepostedOriginal({ original, linkable = true }) {
  const navigate = useNavigate();
  if (!original || original.unavailable) {
    return (
      <Box sx={{ mt: 1, mr: { xs: 0, sm: 2 }, p: 1.5, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider', color: 'text.secondary' }}>
        <Typography variant="body2">
          {original?.reason === 'deleted' ? 'The original post was deleted.' : 'The original post is not available to you.'}
        </Typography>
      </Box>
    );
  }
  const author = original.author || {};
  const name = [author.first_name, author.last_name].filter(Boolean).join(' ') || original.author_name || 'Someone';
  if (original.kind === 'interest') return <RepostedInterest original={original} name={name} linkable={linkable} />;
  const video = findYouTube(original.link_preview?.url, original.body);
  const previewIsVideo = Boolean(parseYouTube(original.link_preview?.url));
  const open = () => navigate(`/feed/${author.id}/posts/${original.id}/`);
  return (
    <Box {...(linkable && { role: 'link', tabIndex: 0, onClick: (e) => { e.stopPropagation(); open(); },
      onKeyDown: (e) => { if (e.key === 'Enter') open(); } })}
      sx={{ mt: 1, mr: linkable ? { xs: 0, sm: 2 } : 0, minWidth: 0, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', ...(linkable && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }) }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Avatar src={author.avatar || undefined} sx={{ width: 26, height: 26, fontSize: 13 }}>{name[0]}</Avatar>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {original.created ? formatDistanceToNow(new Date(original.created), { addSuffix: true }) : ''}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: '12em', overflow: 'hidden', wordBreak: 'break-word', '& p': { mb: 0.75 } }}>
        <LabMarkdown images hashtags>{postMarkdown(original.title ? `**${original.title}**\n\n${original.body}` : original.body)}</LabMarkdown>
      </Box>
      {video && <YouTubeEmbed video={video} title={previewIsVideo ? original.link_preview?.title : undefined} />}
      {!video && original.link_preview?.url && (linkedImageOf(original.link_preview)
        ? <SafeImage src={linkedImageOf(original.link_preview)} alt="" />
        : <LinkPreviewCard preview={original.link_preview} />)}
    </Box>
  );
}
